const {KitModule, registerModule} = window;

class Accordion extends KitModule {
  constructor($element) {
    super($element);
    this.init();
  }

  init() {
    this.openID = null;
    this.isInteractive = this.$element.classList.contains('accordion--interactive-accordion');
    this.behavior = this.$element.dataset.behavior || 'single-active';

    // Save bound event handlers to instance variables so we can cleanly remove them on unmount
    this.boundToggleDisclosure = this.toggleDisclosure.bind(this);
    this.boundBeforePrint = this.beforePrint.bind(this);
    this.boundAfterPrint = this.afterPrint.bind(this);

    this.bindEvents();
  }

  bindEvents() {
    this.$details.forEach(($detail) => {
      if (this.isInteractive && this.behavior === 'expand-all') {
        $detail.addEventListener('click', this.boundToggleDisclosure);
      } else {
        const $title = $detail.querySelector('.accordion__title');
        if ($title) {
          $title.addEventListener('click', this.boundToggleDisclosure);
        }
      }
    });

    // Print
    window.addEventListener('beforeprint', this.boundBeforePrint);
    window.addEventListener('afterprint', this.boundAfterPrint);
  }

  toggleDisclosure(e) {
    const clickedElement = e.currentTarget;
    const clickedDetails = clickedElement.classList.contains('accordion__item') ? clickedElement : clickedElement.closest('.accordion__item');
    if (!clickedDetails) return;

    const isDetails = clickedDetails.tagName.toLowerCase() === 'details';

    // 1. Interactive Image Panel updates
    if (this.isInteractive) {
      const imagePanels = this.$element.querySelectorAll('.accordion__interactive-image-panel');
      if (imagePanels.length > 0) {
        const detailsArray = Array.from(this.$details);
        const index = detailsArray.indexOf(clickedDetails);

        // Deactivate all image panels and hide them from screen readers to prevent redundant readings on mobile
        imagePanels.forEach((panel) => {
          panel.classList.remove('is-active');
          panel.setAttribute('aria-hidden', 'true');
        });

        // Activate the selected panel and expose to screen readers
        // Safely check bounds for index to avoid indexing undefined panel content
        if (index >= 0 && index < imagePanels.length && imagePanels[index]) {
          imagePanels[index].classList.add('is-active');
          imagePanels[index].setAttribute('aria-hidden', 'false');
        }
      }

      if (this.behavior === 'expand-all') {
        this.$details.forEach((d) => d.classList.remove('is-active'));
        clickedDetails.classList.add('is-active');
        return; // nothing more to do for expand-all
      }
    } else if (this.behavior === 'expand-all') {
      return; // static accordion, clicks do nothing
    }

    // 2. Details toggle behaviour
    if (isDetails) {
      if (this.behavior === 'single-active') {
        e.preventDefault();
        const wasOpen = clickedDetails.hasAttribute('open');
        this.closeAllDisclosures();
        if (!wasOpen) {
          clickedDetails.setAttribute('open', '');
        }
      } else if (this.behavior === 'multiple-active') {
        // Native <details> behaviour allows multiple.
        // We do not preventDefault here, letting the browser toggle the 'open' attribute natively.
      }
    }
  }

  openAllDisclosures() {
    this.$details.forEach(($detail) => {
      if ($detail.tagName.toLowerCase() === 'details') {
        $detail.setAttribute('open', '');
      }
    });
  }

  closeAllDisclosures() {
    this.$details.forEach(($detail) => {
      if ($detail.tagName.toLowerCase() === 'details' && $detail.hasAttribute('open')) {
        $detail.removeAttribute('open');
      }
    });
  }

  beforePrint() {
    this.$details.forEach(($detail) => {
      if ($detail.tagName.toLowerCase() === 'details' && $detail.hasAttribute('open')) {
        const $title = $detail.querySelector('.accordion__title');
        if ($title) this.openID = $title.id;
      }
    });
    this.openAllDisclosures();
  }

  afterPrint() {
    this.closeAllDisclosures();
    if (this.openID) {
      const el = document.getElementById(this.openID);
      if (el && el.parentNode) {
        el.parentNode.setAttribute('open', '');
      }
    }
  }

  unmount() {
    // Remove event listeners on unmount to prevent memory leaks
    this.$details.forEach(($detail) => {
      if (this.isInteractive && this.behavior === 'expand-all') {
        $detail.removeEventListener('click', this.boundToggleDisclosure);
      } else {
        const $title = $detail.querySelector('.accordion__title');
        if ($title) {
          $title.removeEventListener('click', this.boundToggleDisclosure);
        }
      }
    });

    window.removeEventListener('beforeprint', this.boundBeforePrint);
    window.removeEventListener('afterprint', this.boundAfterPrint);
  }
}

registerModule('Accordion', Accordion);
